/* eslint-disable jsx-a11y/role-supports-aria-props */
/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { jsx } from '@emotion/core';
import { ThemeConsumer } from '@cvent/carina-theme-provider';
import { ChevronDownIcon } from '@cvent/carina-icon';
import { injectTestId } from '@cvent/nucleus-test-automation';
import Magazine from '@cvent/carina-magazine';
import * as classes from './css';

/**
 * Dropdown control that takes either an array of options or
 * array of children React elements.
 */
export default class Dropdown extends React.Component {
  static propTypes = {
    /** HTML id attribute will be applied as appropriate */
    id: PropTypes.string.isRequired,
    /** options is an array of options* that become the individual dropdown items
     *
     *
     * *options can take one of a few shapes: the 'default' shape for them is an object with a value (any type)
     * and a label: (what actually gets displayed inside of the drop down), which should be either a string or a
     * render-able function/node, plus optional heading an disabled props.
     * The value prop is the only one that is required in this form, if no label is provided,
     * then the value will determine it
     * Alternatively, an option can be just a string, and not an object,
     * in which case the string will determine the other properties
     */
    options: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.any])
            .isRequired,
          label: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.node,
            PropTypes.func
          ]),
          heading: PropTypes.bool,
          disabled: PropTypes.bool
        }),
        PropTypes.string
      ])
    ).isRequired,
    /** Reference a validation message element via the aria-describedby attribute */
    describedBy: PropTypes.string,
    /** Disabled prop will cause entire Dropdown to be disabled */
    disabled: PropTypes.bool,
    /** hasError triggers the error state styles */
    hasError: PropTypes.bool,
    /** Text direction */
    isRtl: PropTypes.bool,
    /** onUpdate returns the selected option */
    onUpdate: PropTypes.func,
    /** Dropdown will display this text if no option/child selected */
    placeholder: PropTypes.string,
    /** required prop will set accessibility-related input attributes */
    required: PropTypes.bool,
    /** selected option value */
    selected: PropTypes.any,
    /** For e2e tests (maps to `data-cvent-id`) */
    testID: PropTypes.string,
    variant: PropTypes.string
  };

  static defaultProps = {
    disabled: false,
    hasError: false,
    isRtl: false,
    placeholder: 'Select option',
    required: false
  };

  state = {
    isOpen: false
  };

  componentDidMount() {
    document.addEventListener('click', this.onClickOutside, false);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.onClickOutside, false);
  }

  onClickOutside = event => {
    if (!ReactDOM.findDOMNode(this).contains(event.target)) {
      this.setState({ isOpen: false });
    }
  };

  onDropdownClick = () => this.setState({ isOpen: !this.state.isOpen });

  onDropdownKeyPress = ({ key }) =>
    (key === 'ArrowDown' || key === 'ArrowUp') &&
    this.setState({ isOpen: true });

  closeDropdown = () => {
    this.outerButton && this.outerButton.focus(); // eslint-disable-line no-unused-expressions
    this.setState({ isOpen: false });
  };

  handleSelect = option => {
    this.props.onUpdate && this.props.onUpdate(option && option.value, option); // eslint-disable-line no-unused-expressions
    this.closeDropdown();
  };

  renderSelectedText = theme => {
    const Option = this.props.options
      ? this.props.options.find(o => {
          return o.value === this.props.selected || o === this.props.selected;
        })
      : [];

    if (typeof Option === 'string') {
      return Option;
    }
    if (typeof Option !== 'undefined') {
      return typeof Option.label === 'function' ? Option.label() : Option.label;
    }

    return (
      <span css={classes.placeholder(theme, this.props.isRtl)}>
        {this.props.placeholder}
      </span>
    );
  };

  render() {
    const {
      disabled,
      hasError,
      id,
      isRtl,
      required,
      testID,
      describedBy,
      variant
    } = this.props;
    return (
      <ThemeConsumer>
        {theme => {
          const selectedText = this.renderSelectedText(theme);
          return (
            <div css={classes.containerCss(theme)}>
              <button
                css={classes.buttonCss({ theme, hasError, isRtl, variant })}
                aria-invalid={hasError}
                {...{ 'aria-describedby': describedBy }}
                aria-label={`Dropdown,${
                  this.props.selected ? ` ${this.props.selected},` : ''
                } ${this.state.isOpen ? '' : 'not '}expanded`}
                aria-haspopup
                id={id}
                tabIndex={disabled ? -1 : 0}
                disabled={disabled}
                {...(required ? { required: true } : {})}
                onClick={this.onDropdownClick}
                onKeyDown={this.onDropdownKeyPress}
                ref={b => {
                  this.outerButton = b;
                }}
                type="button"
                {...injectTestId(testID)}
              >
                {selectedText}
                <span css={classes.iconCss(theme, isRtl)}>
                  <ChevronDownIcon color={theme.font.color.soft} />
                </span>
              </button>
              {this.state.isOpen && (
                <Magazine
                  options={this.props.options || []}
                  theme={theme}
                  handleClose={this.closeDropdown}
                  onUpdate={this.handleSelect}
                  selected={this.props.selected}
                  magazineStyleObject={{
                    width: '100%',
                    marginTop: '-1px',
                    maxHeight: 192,
                    left: 0,
                    top: '100%',
                    boxSizing: 'border-box'
                  }}
                  focusOnMount
                  isRtl={this.props.isRtl}
                  hasError={this.props.hasError}
                  testID={this.props.testID}
                />
              )}
            </div>
          );
        }}
      </ThemeConsumer>
    );
  }
}
